import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm } from "components";
import { createFormHelper } from "lib/Form";
import { AgreementsEditor } from "modules/AgreementsDashboard/components/AgreementsModal/components";
import { useDeleteAgreementMutation } from "modules/AgreementsDashboard/hooks";

import type { CommunicationAgreement } from "models";

type AgreementsFormProps = {
  hasErrors: boolean;
  isPending: boolean;
  agreement?: CommunicationAgreement;
  onClose: () => void;
};

const FormHelper = createFormHelper<CommunicationAgreement>();

export const AgreementsForm = ({ hasErrors, onClose, isPending, agreement }: AgreementsFormProps) => {
  const { t } = useTranslation();

  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const { mutateAsync: deleteAgreement, isPending: isDeletePending } = useDeleteAgreementMutation();

  const isCreate = agreement?.id === -1;

  const handleOpenDeleteModal = () => setShowConfirmDeleteModal(true);
  const handleCloseDeleteModal = () => setShowConfirmDeleteModal(false);
  const handleDeleteAgreement = async () => {
    if (!agreement?.id) return;

    await deleteAgreement(agreement.id);
    onClose();
  };

  const { watch, setValue } = FormHelper.useFormContext();
  const [name, text, visible_online_check_in, visible_keylocker_check_in, visible_kiosk_check_in, visible_desk_check_in, visible_desk_check_out, visible_customcom] =
    watch([
      "name",
      "text",
      "visible_online_check_in",
      "visible_keylocker_check_in",
      "visible_kiosk_check_in",
      "visible_desk_check_in",
      "visible_desk_check_out",
      "visible_customcom",
      "optional_online_check_in",
      "optional_keylocker_check_in",
      "optional_kiosk_check_in",
      "optional_desk_check_in",
      "optional_desk_check_out",
      "optional_customcom"
    ]);

  useEffect(() => {
    const pairs: [boolean, keyof CommunicationAgreement][] = [
      [visible_online_check_in, "optional_online_check_in"],
      [visible_keylocker_check_in, "optional_keylocker_check_in"],
      [visible_kiosk_check_in, "optional_kiosk_check_in"],
      [visible_desk_check_in, "optional_desk_check_in"],
      [visible_desk_check_out, "optional_desk_check_out"],
      [visible_customcom, "optional_customcom"]
    ];

    pairs.forEach(([visible, optional]) => !visible && setValue(optional, false));
  }, [visible_online_check_in, visible_keylocker_check_in, visible_kiosk_check_in, visible_desk_check_in, visible_desk_check_out, visible_customcom]);

  return (
    <>
      <Modal.Header className="padding-container">
        <h3 className="header-title">
          {isCreate ? t("v8_add_new_communication_agreement", "Add new communication agreement") : t("v8_edit_communication_agreement", "Edit communication agreement")}
        </h3>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container">
        <Form.Field required error={!name && hasErrors}>
          <label className="label-padding">{t("v8_name", "Name")} </label>
          <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_name", "Name"), fluid: true }} />
        </Form.Field>
        <span className="checkbox-title">{t("v8_visibility", "Visibility")}</span>
        <Grid columns={6} className="grid-padding">
          <GridColumn>
            <FormHelper.Field.Checkbox name="visible_online_check_in" label={t("v8_online_check_in", "Online Check-in")} />
          </GridColumn>
          <GridColumn>
            <FormHelper.Field.Checkbox name="visible_keylocker_check_in" label={t("v8_keylocker_check_in", "Keylocker Check-In")} />
          </GridColumn>
          <GridColumn>
            <FormHelper.Field.Checkbox name="visible_kiosk_check_in" label={t("v8_kiosk_check_in", "Kiosk Check-In")} />
          </GridColumn>
          <GridColumn>
            <FormHelper.Field.Checkbox name="visible_desk_check_in" label={t("v8_desk_check_in", "Desk Check-In")} />
          </GridColumn>
          <GridColumn>
            <FormHelper.Field.Checkbox name="visible_desk_check_out" label={t("v8_desk_check_out", "Desk Check-Out")} />
          </GridColumn>
          <GridColumn>
            <FormHelper.Field.Checkbox name="visible_customcom" label={t("v8_diagnose_overview", "Diagnose Overview")} />
          </GridColumn>
        </Grid>

        <span className="checkbox-title">{t("v8_optional_choice", "Optional Choice")}</span>
        <Grid columns={6} className="grid-padding">
          <GridColumn>
            <FormHelper.Field.Checkbox
              name="optional_online_check_in"
              label={t("v8_online_check_in", "Online Check-in")}
              formFieldProps={{ disabled: !visible_online_check_in }}
            />
          </GridColumn>
          <GridColumn>
            <FormHelper.Field.Checkbox
              name="optional_keylocker_check_in"
              label={t("v8_keylocker_check_in", "Keylocker Check-In")}
              formFieldProps={{ disabled: !visible_keylocker_check_in }}
            />
          </GridColumn>
          <GridColumn>
            <FormHelper.Field.Checkbox
              name="optional_kiosk_check_in"
              label={t("v8_kiosk_check_in", "Kiosk Check-In")}
              formFieldProps={{ disabled: !visible_kiosk_check_in }}
            />
          </GridColumn>
          <GridColumn>
            <FormHelper.Field.Checkbox
              name="optional_desk_check_in"
              label={t("v8_desk_check_in", "Desk Check-In")}
              formFieldProps={{ disabled: !visible_desk_check_in }}
            />
          </GridColumn>
          <GridColumn>
            <FormHelper.Field.Checkbox
              name="optional_desk_check_out"
              label={t("v8_desk_check_out", "Desk Check-Out")}
              formFieldProps={{ disabled: !visible_desk_check_out }}
            />
          </GridColumn>
          <GridColumn>
            <FormHelper.Field.Checkbox
              name="optional_customcom"
              label={t("v8_diagnose_overview", "Diagnose Overview")}
              formFieldProps={{ disabled: !visible_customcom }}
            />
          </GridColumn>
        </Grid>

        <Form.Field required error={!text && hasErrors}>
          <label className="label-padding">{t("v8_text", "Text")}</label>
          <FormHelper.Controller name="text" render={({ field: { onChange, value } }) => <AgreementsEditor value={value} onChange={onChange} />} />
        </Form.Field>
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="actions-buttons-main-container">
          {!isCreate && (
            <Form.Button color="red" type="button" onClick={handleOpenDeleteModal}>
              {t("v8_delete", "Delete")}
              <Icon className="trash" />
            </Form.Button>
          )}
          <div className="save-cancel-buttons-container">
            <Form.Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Form.Button type="submit" color="green" disabled={!name || !text || isPending} loading={isPending}>
              {isCreate ? t("v8_save", "Save") : t("v8_update", "Update")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={showConfirmDeleteModal}
        handleCancel={handleCloseDeleteModal}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleDeleteAgreement}
        isLoading={isDeletePending}
      />
    </>
  );
};
